/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { LayerChunkProgressInfo } from 'neuroglancer/chunk_manager/base';
import { ChunkManager, ChunkRenderLayerFrontend } from 'neuroglancer/chunk_manager/frontend';
import { LayerSelectedValues } from 'neuroglancer/layer';
import { PickIDManager } from 'neuroglancer/object_picking';
import { RenderLayer } from 'neuroglancer/renderlayer';
import { WatchableRenderLayerTransform } from 'neuroglancer/render_coordinate_transform';
import { RenderScaleHistogram } from 'neuroglancer/render_scale_statistics';
import { forEachVisibleSegment, onTemporaryVisibleSegmentsStateChanged, onVisibleSegmentsStateChanged, VisibleSegmentsState, VISIBLE_SEGMENTS_STATE_PROPERTIES } from 'neuroglancer/segmentation_display_state/base';
import { InlineSegmentNumericalProperty, InlineSegmentProperty, PreprocessedSegmentPropertyMap } from 'neuroglancer/segmentation_display_state/property_map';
import { SegmentationUserLayer, SegmentationUserLayerDisplayState } from 'neuroglancer/segmentation_user_layer';
import { getCssColor, SegmentColorHash } from 'neuroglancer/segment_color';
import { SharedWatchableValue } from 'neuroglancer/shared_watchable_value';
import { TrackableAlphaValue } from 'neuroglancer/trackable_alpha';
import { observeWatchable, registerNestedSync, TrackableValue, WatchableValueInterface } from 'neuroglancer/trackable_value';
import { isWithinSelectionPanel } from 'neuroglancer/ui/selection_details';
import { Uint64Map } from 'neuroglancer/uint64_map';
import { setClipboard } from 'neuroglancer/util/clipboard';
import { packColor, parseRGBColorSpecification, serializeColor, useWhiteBackground } from 'neuroglancer/util/color';
import { RefCounted } from 'neuroglancer/util/disposable';
import { measureElementClone } from 'neuroglancer/util/dom';
import { kOneVec, vec3, vec4 } from 'neuroglancer/util/geom';
import { NullarySignal } from 'neuroglancer/util/signal';
import { Uint64 } from 'neuroglancer/util/uint64';
import { withSharedVisibility } from 'neuroglancer/visibility_priority/frontend';
import { makeCopyButton } from 'neuroglancer/widget/copy_button';
import { makeFilterButton } from 'neuroglancer/widget/filter_button';
import { allToolsButton, local_host_SN_flag, viewer } from 'src/main';
import i18n from '../i18n/i18n';
import { ConfigIndependDialog } from '../ui/congfig-dialog';
import { makeElement } from '../ui/layer_gene_table_tab';
import { float32ToString } from '../util/float32_to_string';
import { hexEncodeByte } from '../util/hex';
import { thousands } from '../util/number_to_string';
import { responseJson } from '../util/http_request';
import { cancellableFetchSpecialOk } from '../util/special_protocol_request';
import { _param as local_href_param } from 'src/main';
import { AnnotationUserLayer } from '../annotation/user_layer';
import { dbStorageMoreWindow } from 'neuroglancer/util/indexDb';

export class Uint64MapEntry {
  constructor(public key: Uint64, public value?: Uint64, public label?: string|undefined) {}
  toString() {
    const {key, value, label} = this;
    let baseString: string;
    if (value === undefined) {
      baseString = `${key}`;
    } else {
      baseString = `${key}→${value}`;
    }
    if (label === undefined) return baseString;
    return `${baseString} ${label}`;
  }
}

export class SegmentSelectionState extends RefCounted {
  selectedSegment = new Uint64();
  baseSelectedSegment = new Uint64();
  hasSelectedSegment = false;
  changed = new NullarySignal();

  get value() {
    return this.hasSelectedSegment ? this.selectedSegment : undefined;
  }

  get baseValue() {
    return this.hasSelectedSegment ? this.baseSelectedSegment : undefined;
  }

  set(value: number|Uint64MapEntry|Uint64|null|undefined, hideSegmentZero = false) {
    const {selectedSegment, baseSelectedSegment} = this;
    let newLow: number = 0, newHigh: number = 0, newBaseLow: number = 0, newBaseHigh: number = 0;
    let hasSelectedSegment: boolean;
    if (value == null) {
      hasSelectedSegment = false;
    } else if (typeof value === 'number') {
      newLow = newBaseLow = (value >>> 0);
      newHigh = newBaseHigh = value < 0 ? 0xffffffff : 0;
      hasSelectedSegment = true;
    } else if (value instanceof Uint64MapEntry) {
      const valueMapped = value.value || value.key;
      newLow = valueMapped.low;
      newHigh = valueMapped.high;
      newBaseLow = value.key.low;
      newBaseHigh = value.key.high;
      hasSelectedSegment = true;
    } else if (value instanceof Uint64) {
      newLow = newBaseLow = value.low;
      newHigh = newBaseHigh = value.high;
      hasSelectedSegment = true;
    } else {
      hasSelectedSegment = false;
    }
    if (hideSegmentZero && newLow === 0 && newHigh === 0) {
      hasSelectedSegment = false;
    }
    if (!hasSelectedSegment) {
      if (this.hasSelectedSegment) {
        this.hasSelectedSegment = false;
        this.changed.dispatch();
      }
    } else if (
        hasSelectedSegment &&
        (!this.hasSelectedSegment || selectedSegment.low !== newLow ||
         selectedSegment.high !== newHigh || baseSelectedSegment.low !== newBaseLow ||
         baseSelectedSegment.high !== newBaseHigh)) {
      selectedSegment.low = newLow;
      selectedSegment.high = newHigh;
      baseSelectedSegment.low = newBaseLow;
      baseSelectedSegment.high = newBaseHigh;
      this.hasSelectedSegment = true;
      this.changed.dispatch();
    }
  }

  isSelected(value: Uint64) {
    return this.hasSelectedSegment && Uint64.equal(value, this.selectedSegment);
  }

  bindTo(layerSelectedValues: LayerSelectedValues, userLayer: SegmentationUserLayer) {
    this.registerDisposer(layerSelectedValues.changed.add(() => {
      const state = layerSelectedValues.get(userLayer);
      let value: any = undefined;
      if (state !== undefined) {
        value = state.value;
      }
      this.set(value, userLayer.displayState.segmentationGroupState.value.hideSegmentZero.value);
    }));
  }
}

export interface SegmentationGroupState extends VisibleSegmentsState {
  /**
   * Maximum length of base-10 representation of id seen.
   */
  maxIdLength: WatchableValueInterface<number>;
  segmentPropertyMap: WatchableValueInterface<PreprocessedSegmentPropertyMap|undefined>;
}

export interface SegmentationColorGroupState {
  segmentColorHash: SegmentColorHash;
  segmentStatedColors: Uint64Map;
  segmentDefaultColor: WatchableValueInterface<vec3|undefined>;
}

export interface SegmentationDisplayState {
  segmentSelectionState: SegmentSelectionState;
  saturation: TrackableAlphaValue;
  baseSegmentColoring: WatchableValueInterface<boolean>;
  segmentationGroupState: WatchableValueInterface<SegmentationGroupState>;
  segmentationColorGroupState: WatchableValueInterface<SegmentationColorGroupState>;

  selectSegment: (id: Uint64, pin: boolean|'toggle') => void;
  filterBySegmentLabel: (id: Uint64) => void;
  moveToSegment: (id: Uint64) => void;

  // Indirect properties
  hideSegmentZero: WatchableValueInterface<boolean>;
  segmentColorHash: WatchableValueInterface<number>;
  segmentStatedColors: WatchableValueInterface<Uint64Map>;
  segmentDefaultColor: WatchableValueInterface<vec3|undefined>;
}

export function resetTemporaryVisibleSegmentsState(state: VisibleSegmentsState) {
  state.useTemporarySegmentEquivalences.value = false;
  state.useTemporaryVisibleSegments.value = false;
  state.temporaryVisibleSegments.clear();
  state.temporarySegmentEquivalences.clear();
}

/// Converts a segment id to a Uint64MapEntry or Uint64 (if Uint64MapEntry would add no additional
/// information).
export function maybeAugmentSegmentId(
    displayState: SegmentationDisplayState|undefined|null, value: number|Uint64,
    mustCopy: boolean = false): Uint64|Uint64MapEntry {
  let id: Uint64;
  let mappedValue: Uint64;
  let mapped: Uint64|undefined;
  let label: string|undefined;
  if (typeof value === 'number') {
    id = new Uint64((value >>> 0), value < 0 ? 0xffffffff : 0);
  } else if (typeof value === 'string') {
    id = Uint64.parseString(value);
  } else {
    id = mustCopy ? value.clone() : value;
  }
  if (displayState == null) return id;
  const {segmentEquivalences, segmentPropertyMap: {value: segmentPropertyMap}} =
      displayState.segmentationGroupState.value;
  if (segmentEquivalences.size !== 0) {
    mappedValue = segmentEquivalences.get(id);
    if (Uint64.equal(mappedValue, id)) {
      mapped = undefined;
    } else {
      mapped = mappedValue;
    }
  } else {
    mappedValue = id;
  }
  label = segmentPropertyMap?.getSegmentLabel(mappedValue);
  if (label === undefined && mapped == undefined) {
    return id;
  }  
  return new Uint64MapEntry(id, mapped, label);
}

/// Converts a plain segment id to a Uint64MapEntry.
export function augmentSegmentId(
    displayState: SegmentationDisplayState|undefined|null,
    value: number|Uint64|Uint64MapEntry): Uint64MapEntry {
  if (value instanceof Uint64MapEntry) return value;
  let newValue = maybeAugmentSegmentId(displayState, value);
  if (newValue instanceof Uint64) {
    return new Uint64MapEntry(newValue);
  }
  return newValue;
}

export function updateIdStringWidth(
    idStringWidth: WatchableValueInterface<number>, idString: string) {
  const {length} = idString;
  if (idStringWidth.value < length) {
    idStringWidth.value = length;
  }
}

export function bindSegmentListWidth(displayState: SegmentationDisplayState, element: HTMLElement) {
  return observeWatchable(
      width => element.style.setProperty('--neuroglancer-segment-list-width', `${width}ch`),
      displayState.segmentationGroupState.value.maxIdLength);
}

const segmentWidgetTemplate = (() => {
  const template = document.createElement('div');
  template.classList.add('neuroglancer-segment-list-entry');
  const stickyContainer = document.createElement('div');
  stickyContainer.classList.add('neuroglancer-segment-list-entry-sticky');
  template.appendChild(stickyContainer);
  const copyButton = makeCopyButton({
    title: `Copy segment ID`,
  });
  copyButton.classList.add('neuroglancer-segment-list-entry-copy');
  const copyContainer = document.createElement('div');
  copyContainer.classList.add('neuroglancer-segment-list-entry-copy-container');
  const copyIndex = copyContainer.childElementCount;
  copyContainer.appendChild(copyButton);
  const copyContainerIndex = stickyContainer.childElementCount;
  stickyContainer.appendChild(copyContainer);
  const visibleIndex = stickyContainer.childElementCount;
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.title = 'Toggle segment visibility';
  checkbox.classList.add('neuroglancer-segment-list-entry-visible-checkbox', 'ng-common-checkbox', 'ng-common-checkbox');
  stickyContainer.appendChild(checkbox);
  // label
  const nameElement = document.createElement('input');
  nameElement.classList.add('neuroglancer-segment-list-entry-name');
  const labelIndex = template.childElementCount;
  template.appendChild(nameElement);
  // count
  const countElement = document.createElement('span');
  countElement.classList.add('neuroglancer-segment-list-entry-count');
  const countIndex = template.childElementCount;
  template.appendChild(countElement);
  // 增加color自定义dom
  // const colorInput = document.createElement('input');
  // colorInput.type = 'color';
  // colorInput.title = 'set segment color';
  // colorInput.classList.add('neuroglancer-segment-list-entry-visible-colorinput');
  // template.appendChild(colorInput);
  const colorIndex = template.childElementCount;
  const colorSpan = document.createElement('span');
  colorSpan.title = 'set segment color';
  colorSpan.classList.add('neuroglancer-segment-list-entry-visible-colorinput');
  template.appendChild(colorSpan);
  // ***
  const idContainer = document.createElement('div');
  idContainer.classList.add('neuroglancer-segment-list-entry-id-container');
  const idContainerIndex = stickyContainer.childElementCount;
  stickyContainer.appendChild(idContainer);
  const idElement = document.createElement('div');
  idElement.classList.add('neuroglancer-segment-list-entry-id');
  const idIndex = idContainer.childElementCount;
  idContainer.appendChild(idElement);
  // const nameElement = document.createElement('span');
  const filterElement = makeFilterButton({
    title: 'Filter by label',
  });
  filterElement.classList.add('neuroglancer-segment-list-entry-filter');
  const filterIndex = template.childElementCount;
  template.appendChild(filterElement);

  return {
    template,
    copyContainerIndex,
    copyIndex,
    visibleIndex,
    idContainerIndex,
    idIndex,
    colorIndex,
    labelIndex,
    filterIndex,
    unmappedIdIndex: -1,
    unmappedCopyIndex: -1,
    labelMap: new Map,
    countIndex
  };
})();

const segmentWidgetTemplateWithUnmapped = (() => {
  const t = segmentWidgetTemplate;
  const template = t.template.cloneNode(/*deep=*/ true) as HTMLDivElement;
  const stickyContainer = template.children[0] as HTMLElement;
  const idContainer = stickyContainer.children[t.idContainerIndex] as HTMLElement;
  const unmappedIdIndex = idContainer.childElementCount;
  const unmappedIdElement =
      idContainer.children[t.idIndex].cloneNode(/*deep=*/ true) as HTMLElement;
  unmappedIdElement.classList.add('neuroglancer-segment-list-entry-unmapped-id');
  idContainer.appendChild(unmappedIdElement);
  const copyContainer = stickyContainer.children[t.copyContainerIndex] as HTMLElement;
  const unmappedCopyIndex = copyContainer.childElementCount;
  copyContainer.appendChild(copyContainer.children[t.copyIndex].cloneNode(/*deep=*/ true));
  return {...t, template, unmappedIdIndex, unmappedCopyIndex};
})();

export type SegmentWidgetTemplate = typeof segmentWidgetTemplate;

interface SegmentWidgetWithExtraColumnsTemplate extends SegmentWidgetTemplate {
  numericalPropertyIndices: number[];
}

export function segmentWidgetTemplateWithExtraColumns(numExtraColumns: number):
    SegmentWidgetWithExtraColumnsTemplate {
  let origTemplate = segmentWidgetTemplate;
  const templateElement = origTemplate.template.cloneNode(/*deep=*/ true) as HTMLDivElement;
  const numericalPropertyIndices: number[] = [];
  for (let i = 0; i < numExtraColumns; ++i) {
    numericalPropertyIndices.push(templateElement.childElementCount);
    const child = document.createElement('div');
    child.classList.add('neuroglancer-segment-list-entry-extra-property');
    child.style.width =
        `max(var(--neuroglancer-column-${i}-width), var(--neuroglancer-column-${i}-label-width))`;
    templateElement.appendChild(child);
  }
  return {...origTemplate, template: templateElement, numericalPropertyIndices};
}

const cachedRegisterSegmentWidgetEventHandlers = new WeakMap<
    SegmentationDisplayState, (element: HTMLElement, template: SegmentWidgetTemplate) => void>();

function makeRegisterSegmentWidgetEventHandlers(displayState: SegmentationDisplayState) {
  // // const onMouseEnter = (event: Event) => {
  // //   const entryElement = event.currentTarget as HTMLElement;
  // //   const idString = entryElement.dataset.id!;
  // //   const id = tempStatedColor;
  // //   id.tryParseString(idString);
  // //   displayState.segmentSelectionState.set(id);
  // //   if (!isWithinSelectionPanel(entryElement)) {
  // //     displayState.selectSegment(id, false);
  // //   }
  // // };

  const selectHandler = (event: Event) => {
    const entryElement = event.currentTarget as HTMLElement;
    const idString = entryElement.dataset.id!;
    const id = tempStatedColor;
    id.tryParseString(idString);
    displayState.selectSegment(id, isWithinSelectionPanel(entryElement) ? 'toggle' : true);
  };

  // // const onMouseLeave = () => {
  // //   displayState.segmentSelectionState.set(null);
  // // };

  const getEntryElement = (event: Event): HTMLElement => {
    return (event.currentTarget as HTMLElement).closest('.neuroglancer-segment-list-entry') as
        HTMLElement;
  };

  const copyHandler = (event: Event) => {
    const entryElement = getEntryElement(event);
    setClipboard(entryElement.dataset.id!);
    event.stopPropagation();
  };

  const unmappedCopyHandler = (event: Event) => {
    const entryElement = getEntryElement(event);
    setClipboard(entryElement.dataset.unmappedId!);
    event.stopPropagation();
  };

  const visibleCheckboxHandler = async (event: Event) => {
    const entryElement = getEntryElement(event);
    const idString = entryElement.dataset.id!;
    const id = tempStatedColor;
    id.tryParseString(idString);
    const {visibleSegments} = displayState.segmentationGroupState.value;
    const select_name = (displayState as SegmentationUserLayerDisplayState).layer.genename
    const cluster = select_name.includes('cell_cluster') ? select_name.replace('cell_cluster_layer','UMAP') : select_name.replace('UMAP','cell_cluster_layer')
    const umap = viewer.layerManager.managedLayers.filter((item:any)=> item.name === cluster)[0];
    if(umap){
      const umap_visibleSegments = ( (umap.layer as AnnotationUserLayer).displayState as SegmentationDisplayState).segmentationGroupState.value.visibleSegments;
      umap_visibleSegments.set(id, !visibleSegments.has(id));
    }
    // const {globalVisibleSegments} = viewer
    // globalVisibleSegments.setValue(id, !visibleSegments.has(id))
    visibleSegments.set(id, !visibleSegments.has(id));

    // cellbin聚类热图，自动增强功能，判断是否有聚类已经勾选
    const cellbinLayer = (displayState as SegmentationUserLayerDisplayState).layer;
    if(cellbinLayer.managedLayer.name.includes('cell_cluster_layer') && cellbinLayer.displayState.segmentationGroupState.value.visibleSegments.size > 0) {
      viewer.imgFadeNeeded.value = true;
    } else {
      viewer.imgFadeNeeded.value = false;
    }

    event.stopPropagation();
    viewer.resetoreJson(viewer.state.toJSON());
    if(visibleSegments.hasID.length && !local_href_param.visible && viewer.binSize.value !== 'cellbin'){
      let req1 = async (): Promise<any> => {
        const url = `${local_host_SN_flag}/annotation/get_gene_coordinate_by_cluster`;
        return await cancellableFetchSpecialOk(undefined, url, {
          method: 'post',
          body: JSON.stringify({
            file_h5ad: allToolsButton.h5ad_file,
            cluster_id: visibleSegments.hasID.map((item:number)=>item - 1)
          }),
          headers: { "Content-Type": "application/json" }
        }, responseJson);
      }
      let res1 = await req1();
      if (res1.code === 0) {
        // const data = res1.data[0]?.x ? [res1.data.map((item:any)=>item.x),res1.data.map((item:any)=>item.y)] : res1.data;
        if(!local_href_param.visible){
          const obj = {
            href: '',
            value: res1.data
          }
          if (dbStorageMoreWindow) {
            await dbStorageMoreWindow.update('layerName', {name: 'value', data: obj})
          }
          localStorage.setItem('layerName', new Date().toLocaleString());
        }
      }
    }else{
      if(!local_href_param.visible){
        const obj = {
          href: '',
          value: [[],[]]
        }
        if (dbStorageMoreWindow) {
          await dbStorageMoreWindow.update('layerName', {name: 'value', data: obj})
        }
        localStorage.setItem('layerName', new Date().toLocaleString());
      }
    }
  };

  const filterHandler = (event: Event) => {
    const entryElement = getEntryElement(event);
    const idString = entryElement.dataset.id!;
    const id = tempStatedColor
    id.tryParseString(idString);
    id.tryParseString(idString);
    displayState.filterBySegmentLabel(id);
    event.stopPropagation();
  };
  // seg自定义颜色事件处理
  const colorHandler = (event: Event) => {
    // const {globalStatedColor} = viewer
    const entryElement = getEntryElement(event); 
    const colorStr = (event.target as HTMLInputElement).style.backgroundColor;
    const idString = entryElement.dataset.id!;
    const id = Uint64.parseString(String(idString));
    id.tryParseString(idString);
    const colorGroupState = displayState.segmentationColorGroupState.value;
    const {segmentStatedColors} = colorGroupState;
    const color = parseRGBColorSpecification(String(colorStr));
    segmentStatedColors.delete(id)
    segmentStatedColors.set(id, new Uint64(packColor(color)));
    // globalStatedColor.deleteValue(id)
    // globalStatedColor.setValue(id, new Uint64(packColor(color)))
  }

  const colorChangeHandler = (colorSpan: any) => {
    viewer.clearActiveTool(colorSpan);
    const entryElement = colorSpan.closest('.neuroglancer-segment-list-entry') as HTMLElement; 
    const color = colorSpan.style.backgroundColor;
    // console.log((event.target as HTMLInputElement).value)
    const idString = entryElement.dataset.id!;
    // let json = viewer.state.toJSON();
    // json.selectedLayer = {
    //   layer: `cell_seg_${idString}`,
    //   visible: true
    // }
    viewer.showSingleGene.value = `cell_seg_${idString}`;
    viewer.configStorage.registParentSingle(`cell_seg_${idString}`, colorSpan)
    const configDialog = new ConfigIndependDialog(colorSpan, new CustomSegColorWidget(parseRGBColorSpecification(color), idString, displayState as SegmentationUserLayerDisplayState).element, {position: 'left', className: 'segColor-configdialog'});
    configDialog?.buttonClose.addEventListener('click',()=>{
      viewer.configStorage.setActivedTool('')
      viewer.clearActiveTool();
      viewer.showSingleGene.value = undefined;
    })
  }

  const onMousedown = (event: MouseEvent) => {
    if (event.button !== 2 || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
      return;
    }
    const entryElement = event.currentTarget as HTMLElement;
    const idString = entryElement.dataset.id!;
    const id = tempStatedColor
    id.tryParseString(idString);
    displayState.moveToSegment(id);
  };

  return (element: HTMLElement, template: SegmentWidgetTemplate) => {
    const {children} = element;
    const stickyChildren = children[0].children;
    element.addEventListener('mousedown', onMousedown);
    const copyContainer = stickyChildren[template.copyContainerIndex] as HTMLElement;
    if (template.unmappedCopyIndex !== -1) {
      copyContainer.children[template.unmappedCopyIndex].addEventListener(
          'click', unmappedCopyHandler);
    }
    copyContainer.children[template.copyIndex].addEventListener('click', copyHandler);
    // // element.addEventListener('mouseenter', onMouseEnter);
    // // element.addEventListener('mouseleave', onMouseLeave);
    stickyChildren[template.visibleIndex].addEventListener('click', visibleCheckboxHandler);
    children[template.colorIndex].addEventListener('click', ()=>{
      colorChangeHandler(children[template.colorIndex])
    });
    children[template.colorIndex].addEventListener('click', colorHandler);
    children[template.filterIndex].addEventListener('click', filterHandler);
    element.addEventListener('action:select-position', selectHandler);
  };
}

export class SegmentWidgetFactory<Template extends SegmentWidgetTemplate> {
  private registerEventHandlers: undefined|
      ((element: HTMLElement, template: SegmentWidgetTemplate) => void);
  genefile = '';
  constructor(
      public displayState: SegmentationDisplayState|undefined, protected template: Template) {
    if (displayState !== undefined) {
      let r = cachedRegisterSegmentWidgetEventHandlers.get(displayState);
      if (r === undefined) {
        r = makeRegisterSegmentWidgetEventHandlers(displayState);
        cachedRegisterSegmentWidgetEventHandlers.set(displayState, r);
      }
      this.genefile = (displayState as SegmentationUserLayerDisplayState).layer.genefile;
      this.registerEventHandlers = r;
    }
  }

  static make(displayState: SegmentationDisplayState|undefined, includeUnmapped: boolean) {
    return new SegmentWidgetFactory(
        displayState, includeUnmapped ? segmentWidgetTemplateWithUnmapped : segmentWidgetTemplate);
  }

  get(rawId: Uint64|number): HTMLDivElement {
    const {displayState} = this;
    return this.getWithNormalizedId(augmentSegmentId(displayState, rawId));
  }

  getWithNormalizedId(normalizedId: Uint64MapEntry): HTMLDivElement {
    const {displayState, template} = this;
    const container = template.template.cloneNode(/*deep=*/ true) as HTMLDivElement;
    const id = normalizedId.key;
    const mapped = normalizedId.value ?? id;
    const mappedIdString = mapped.toString();
    container.dataset.id = mappedIdString;
    const {children} = container;
    const stickyChildren = children[0].children;
    const idContainer = stickyChildren[template.idContainerIndex] as HTMLElement;
    idContainer.children[template.idIndex].textContent = mappedIdString;
    const {unmappedIdIndex} = template;
    if (displayState !== undefined) {
      this.registerEventHandlers!(container, template);
    } else {
      (stickyChildren[template.visibleIndex] as HTMLElement).style.display = 'none';
    }
    if (unmappedIdIndex !== -1) {
      const unmappedIdElement = idContainer.children[unmappedIdIndex] as HTMLElement;
      if (!Uint64.equal(id, mapped)) {
        const unmappedIdString = id.toString();
        container.dataset.unmappedId = unmappedIdString;
        unmappedIdElement.textContent = unmappedIdString;
        if (displayState !== undefined) {
          updateIdStringWidth(
              displayState.segmentationGroupState.value.maxIdLength, unmappedIdString);
        }
      } else {
        unmappedIdElement.style.display = 'none';
        const copyContainer = stickyChildren[template.copyContainerIndex] as HTMLElement;
        (copyContainer.children[template.unmappedCopyIndex] as HTMLElement).style.display = 'none';
      }
    }
    children[template.labelIndex].textContent = (normalizedId.label)?? '';
    const cluster_layer_file = (displayState as SegmentationUserLayerDisplayState)?.layer?.genefile;
    const self_newlabel = viewer.binSize.value === 'cellbin' ? viewer.self_newlabel[cluster_layer_file] : viewer.bin200_self_newlabel[cluster_layer_file]?.[viewer.binSize.value as string];
    children[template.labelIndex].setAttribute('value',(self_newlabel && self_newlabel[mappedIdString] || normalizedId.label) ?? '');
    children[template.labelIndex].addEventListener('change',async (e:Event)=>{
      if (self_newlabel) {
        (self_newlabel as any)[mappedIdString] = (e.target as HTMLInputElement).value;
      }
      if(viewer.binSize.value === 'cellbin'){
        viewer.self_newlabel[cluster_layer_file] = self_newlabel
      }else{
        if (viewer.bin200_self_newlabel[cluster_layer_file]) {
          viewer.bin200_self_newlabel[cluster_layer_file][viewer.binSize.value as string] = self_newlabel;
        }
        let obj: any = {bin200_self_newlabel: viewer.bin200_self_newlabel};
        let tempData: any = await dbStorageMoreWindow.readByIndex('layerName', 'name', 'value'), data: any;
        tempData && (data = tempData.data);
        if (data && data.value) {
          obj.value = data.value;
        }
        if (dbStorageMoreWindow) {
          await dbStorageMoreWindow.update('layerName', {name: 'value', data: obj});
        }
        localStorage.setItem('layerName', new Date().toLocaleString());
      }
      if(document.getElementsByClassName('color-widget-a-'+mappedIdString)[0]){
        (document.getElementsByClassName('color-widget-a-'+mappedIdString)[0] as HTMLInputElement).innerHTML = (e.target as HTMLInputElement).value;
      }
    });
    if (displayState !== undefined) {
      const {segmentPropertyMap: {value: segmentPropertyMap}} = displayState.segmentationGroupState.value;
      if (segmentPropertyMap !== undefined) {
        const {segmentPropertyMap: {inlineProperties: inlineProperties}} = segmentPropertyMap
        const countValue = inlineProperties?.cellCount || inlineProperties?.binCount
        children[template.countIndex].textContent = countValue ? `(${thousands(countValue[mappedIdString])})` : '';
      }
    }
    
    if (displayState !== undefined) {
      this.updateWithId(container, mapped);
      updateIdStringWidth(displayState.segmentationGroupState.value.maxIdLength, mappedIdString);
    }
    return container;
  }

  update(container: HTMLElement) {
    const id = tempStatedColor;
    const idString = container.dataset.id;
    if (idString === undefined) return;
    id.parseString(idString);
    this.updateWithId(container, id);
  }

  private updateWithId(container: HTMLElement, mapped: Uint64) {
    const {children} = container;
    const stickyChildren = children[0].children;
    const {template} = this;
    const {displayState} = this;
    const {segmentSelectionState} = displayState!;
    const {visibleSegments} = displayState!.segmentationGroupState.value;
    (stickyChildren[template.visibleIndex] as HTMLInputElement).checked =
        visibleSegments.has(mapped);
    container.dataset.selected = (segmentSelectionState.hasSelectedSegment &&
                                  Uint64.equal(segmentSelectionState.selectedSegment, mapped))
                                     .toString();
    const idContainer = stickyChildren[template.idContainerIndex] as HTMLElement;
    const orignColor = getBaseObjectColor(this.displayState, mapped) as vec3;
    setSegmentIdElementStyle(
      (idContainer.children[template.idIndex] as HTMLElement),
      orignColor);
    const color = serializeColor(vec3.fromValues(orignColor[0], orignColor[1], orignColor[2]));
    const colorSpan = (children[template.colorIndex] as HTMLInputElement);
    colorSpan.style.backgroundColor = color;
    // 增加自定义颜色选项
    // const colorElement = new ColorWidget()

    const {unmappedIdIndex} = template;
    if (unmappedIdIndex !== -1) {
      let unmappedIdString: string|undefined;
      let color: vec3;
      if (displayState!.baseSegmentColoring.value &&
          (unmappedIdString = container.dataset.unmappedId) !== undefined) {
        const unmappedId = tempStatedColor;
        unmappedId.parseString(unmappedIdString);
        color = getBaseObjectColor(this.displayState, unmappedId) as vec3;
      } else {
        color = kOneVec;
      }
      setSegmentIdElementStyle(idContainer.children[unmappedIdIndex] as HTMLElement, color);
    }
  }
}

function setSegmentIdElementStyle(element: HTMLElement, color: vec3) {
  element.style.backgroundColor = getCssColor(color);
  element.style.color = useWhiteBackground(color) ? 'white' : 'black';
}

export class SegmentWidgetWithExtraColumnsFactory extends
    SegmentWidgetFactory<SegmentWidgetWithExtraColumnsTemplate> {
  segmentPropertyMap: PreprocessedSegmentPropertyMap|undefined;
  numericalProperties: InlineSegmentNumericalProperty[];
  numericalPropertyWidths: number[];
  parentElement: HTMLElement;
  constructor(
      displayState: SegmentationDisplayState, parentElement: HTMLElement,
      includeProperty: (property: InlineSegmentProperty) => boolean) {
    const segmentPropertyMap = displayState.segmentationGroupState.value.segmentPropertyMap.value;
    const numericalProperties =
        (segmentPropertyMap?.numericalProperties ?? []).filter(includeProperty);
    const template = segmentWidgetTemplateWithExtraColumns(numericalProperties.length);
    super(displayState, template);
    this.parentElement = parentElement;
    this.segmentPropertyMap = segmentPropertyMap;
    this.numericalProperties = numericalProperties;
    const numericalPropertyWidths = this.numericalPropertyWidths =
        new Array(this.numericalProperties.length);
    numericalPropertyWidths.fill(0);
  }

  getWithNormalizedId(normalizedId: Uint64MapEntry): HTMLDivElement {
    const container = super.getWithNormalizedId(normalizedId);
    const {numericalProperties} = this;
    const {numericalPropertyIndices} = this.template;
    if (numericalPropertyIndices.length > 0) {
      const index =
          this.segmentPropertyMap?.getSegmentInlineIndex(normalizedId.value ?? normalizedId.key) ??
          -1;
      if (index !== -1) {
        const {numericalPropertyWidths} = this;
        for (let i = 0, n = numericalPropertyIndices.length; i < n; ++i) {
          const value = numericalProperties[i].values[index];
          if (!isNaN(value)) {
            const s = value.toString();
            const w = s.length;
            if (w > numericalPropertyWidths[i]) {
              numericalPropertyWidths[i] = w;
              this.parentElement.style.setProperty(`--neuroglancer-column-${i}-width`, `${w}ch`);
            }
            container.children[numericalPropertyIndices[i]].textContent = s;
          }
        }
      }
    }
    return container;
  }

  private makeHeaderLabel(id: string, widthProperty: string, parent: HTMLElement) {
    const label = document.createElement('span');
    label.textContent = id;
    label.classList.add('neuroglancer-segment-list-header-label');
    label.classList.add('neuroglancer-segment-list-header-label');
    if (id === 'label') {
      parent.style.textAlign = 'left';
    }
    const sortIcon = document.createElement('span');
    sortIcon.classList.add('neuroglancer-segment-list-header-label-sort');
    label.appendChild(sortIcon);
    sortIcon.textContent = '▲';
    const width = measureElementClone(label).width;
    this.parentElement.style.setProperty(widthProperty, `${width}px`);
    parent.appendChild(label);
    return {id, label, sortIcon};
  }

  getHeader() {
    const {template} = this;
    const container = template.template.cloneNode(/*deep=*/ true) as HTMLDivElement;
    const {children} = container;
    const stickyChildren = children[0].children;
    const copyContainer = stickyChildren[template.copyContainerIndex] as HTMLElement;
    copyContainer.style.visibility = 'hidden';
    (stickyChildren[template.visibleIndex] as HTMLElement).style.visibility = 'hidden';
    (children[template.colorIndex] as HTMLElement).style.visibility = 'hidden';
    (children[template.filterIndex] as HTMLElement).style.visibility = 'hidden';
    const idContainer = stickyChildren[template.idContainerIndex] as HTMLElement;
    const propertyLabels = [
      this.makeHeaderLabel(
          'id', '--neuroglancer-id-column-label-width',
          idContainer.children[template.idIndex] as HTMLElement),
      this.makeHeaderLabel(
          'label', '--neuroglancer-label-column-label-width',
          children[template.labelIndex] as HTMLElement),
    ];
    const {numericalProperties} = this;
    const {numericalPropertyIndices} = this.template;
    for (let i = 0, n = numericalPropertyIndices.length; i < n; ++i) {
      const property = numericalProperties[i];
      const headerLabel = this.makeHeaderLabel(
          property.id, `--neuroglancer-column-${i}-label-width`,
          container.children[numericalPropertyIndices[i]] as HTMLElement);
      const {description} = property;
      if (description) {
        headerLabel.label.title = description;
      }
      propertyLabels.push(headerLabel);
    }
    return {container, propertyLabels};
  }
}

export function makeSegmentWidget(
    displayState: SegmentationDisplayState|undefined|null, normalizedId: Uint64MapEntry) {
  const factory = SegmentWidgetFactory.make(displayState ?? undefined, /*includeUnmapped=*/ true);
  return factory.getWithNormalizedId(normalizedId);
}

export interface SegmentationDisplayStateWithAlpha extends SegmentationDisplayState {
  objectAlpha: TrackableAlphaValue;
}

export interface SegmentationDisplayState3D extends SegmentationDisplayStateWithAlpha {
  transform: WatchableRenderLayerTransform;
  renderScaleHistogram: RenderScaleHistogram;
  renderScaleTarget: TrackableValue<number>;
  // Specifies whether to write to the pick buffer when rendering with transparency.  This prevents
  // any object behind the transparent object from being picked.  When not rendering with
  // transparency, the pick buffer is always written (since there is no downside).
  transparentPickEnabled: WatchableValueInterface<boolean>;
}

export function registerCallbackWhenSegmentationDisplayStateChanged(
    displayState: SegmentationDisplayState, context: RefCounted, callback: () => void) {
  context.registerDisposer(registerNestedSync((c, groupState) => {
    onVisibleSegmentsStateChanged(c, groupState, callback);
  }, displayState.segmentationGroupState));
  context.registerDisposer(registerNestedSync((c, colorGroupState) => {
    c.registerDisposer(colorGroupState.segmentColorHash.changed.add(callback));
    c.registerDisposer(colorGroupState.segmentDefaultColor.changed.add(callback));
    c.registerDisposer(colorGroupState.segmentStatedColors.changed.add(callback));
  }, displayState.segmentationColorGroupState));
  context.registerDisposer(displayState.saturation.changed.add(callback));
  context.registerDisposer(displayState.segmentSelectionState.changed.add(callback));
  context.registerDisposer(displayState.baseSegmentColoring.changed.add(callback));
}

export function registerRedrawWhenSegmentationDisplayStateChanged(
    displayState: SegmentationDisplayState, renderLayer: {redrawNeeded: NullarySignal}&RefCounted) {
  const callback = renderLayer.redrawNeeded.dispatch;
  registerCallbackWhenSegmentationDisplayStateChanged(displayState, renderLayer, callback);
  renderLayer.registerDisposer(registerNestedSync((c, groupState) => {
    onTemporaryVisibleSegmentsStateChanged(c, groupState, callback);
  }, displayState.segmentationGroupState));
}

export function registerRedrawWhenSegmentationDisplayStateWithAlphaChanged(
    displayState: SegmentationDisplayStateWithAlpha,
    renderLayer: {redrawNeeded: NullarySignal}&RefCounted) {
  registerRedrawWhenSegmentationDisplayStateChanged(displayState, renderLayer);
  renderLayer.registerDisposer(
      displayState.objectAlpha.changed.add(renderLayer.redrawNeeded.dispatch));
}

export function registerRedrawWhenSegmentationDisplayState3DChanged(
    displayState: SegmentationDisplayState3D,
    renderLayer: {redrawNeeded: NullarySignal}&RefCounted) {
  registerRedrawWhenSegmentationDisplayStateWithAlphaChanged(displayState, renderLayer);
  renderLayer.registerDisposer(
      displayState.transform.changed.add(renderLayer.redrawNeeded.dispatch));
  renderLayer.registerDisposer(
      displayState.renderScaleTarget.changed.add(renderLayer.redrawNeeded.dispatch));
  renderLayer.registerDisposer(
      displayState.transparentPickEnabled.changed.add(renderLayer.redrawNeeded.dispatch));
}

/**
 * Temporary values used by getObjectColor.
 */
const tempColor = vec4.create();
const tempStatedColor = new Uint64();

export function getBaseObjectColor(
    displayState: SegmentationDisplayState|undefined|null, objectId: Uint64,
    color: Float32Array = tempColor) {
  if (displayState == null) {
    color.fill(1);
    return color;
  };
  const colorGroupState = displayState.segmentationColorGroupState.value;
  const {segmentStatedColors} = colorGroupState;
  if (segmentStatedColors.size !== 0 &&
      colorGroupState.segmentStatedColors.get(objectId, tempStatedColor)) {
    // If displayState maps the ID to a color, use it
    color[0] = ((tempStatedColor.low & 0x0000ff)) / 255.0;
    color[1] = ((tempStatedColor.low & 0x00ff00) >>> 8) / 255.0;
    color[2] = ((tempStatedColor.low & 0xff0000) >>> 16) / 255.0;
    return color;
  }
  const segmentDefaultColor = colorGroupState.segmentDefaultColor.value;
  if (segmentDefaultColor !== undefined) {
    color[0] = segmentDefaultColor[0];
    color[1] = segmentDefaultColor[1];
    color[2] = segmentDefaultColor[2];
    return color;
  }
  colorGroupState.segmentColorHash.compute(color, objectId);
  return color;
}

/**
 * Returns the alpha-premultiplied color to use.
 */
export function getObjectColor(
    displayState: SegmentationDisplayState, objectId: Uint64, alpha: number = 1) {
  const color = tempColor;
  color[3] = alpha;
  getBaseObjectColor(displayState, objectId, color);
  let saturation = displayState.saturation.value;
  if (displayState.segmentSelectionState.isSelected(objectId)) {
    if (saturation > 0.5) {
      saturation = saturation -= 0.5;
    } else {
      saturation += 0.5;
    }
  }
  for (let i = 0; i < 3; ++i) {
    color[i] = color[i] * saturation + (1 - saturation);
  }

  color[0] *= alpha;
  color[1] *= alpha;
  color[2] *= alpha;
  // color[0] = 0;
  // color[1] = 1;
  // color[2] = 1;
  return color;
}

export function sendVisibleSegmentsState(state: VisibleSegmentsState, options: any = {}) {
  for (const property of VISIBLE_SEGMENTS_STATE_PROPERTIES) {
    options[property] = state[property].rpcId;
  }
  return options;
}

const Base = withSharedVisibility(ChunkRenderLayerFrontend);
export class SegmentationLayerSharedObject extends Base {
  constructor(
      public chunkManager: ChunkManager, public displayState: SegmentationDisplayState3D,
      chunkRenderLayer: LayerChunkProgressInfo) {
    super(chunkRenderLayer);
  }

  initializeCounterpartWithChunkManager(options: any) {
    let {displayState} = this;
    options['chunkManager'] = this.chunkManager.rpcId;
    sendVisibleSegmentsState(displayState.segmentationGroupState.value, options);
    options['transform'] =
        this.registerDisposer(SharedWatchableValue.makeFromExisting(
                                  this.chunkManager.rpc!, this.displayState.transform))
            .rpcId;
    options['renderScaleTarget'] =
        this.registerDisposer(SharedWatchableValue.makeFromExisting(
                                  this.chunkManager.rpc!, this.displayState.renderScaleTarget))
            .rpcId;
    super.initializeCounterpart(this.chunkManager.rpc!, options);
  }
}

export function forEachVisibleSegmentToDraw(
    displayState: SegmentationDisplayState3D, renderLayer: RenderLayer, emitColor: boolean,
    pickIDs: PickIDManager|undefined,
    callback: (
        objectId: Uint64, color: vec4|undefined, pickIndex: number|undefined,
        rootObjectId: Uint64) => void) {
  const alpha = Math.min(1, displayState.objectAlpha.value);
  const baseSegmentColoring = displayState.baseSegmentColoring.value;
  forEachVisibleSegment(displayState.segmentationGroupState.value, (objectId, rootObjectId) => {
    let pickIndex = pickIDs?.registerUint64(renderLayer, objectId);
    let color = emitColor ?
        getObjectColor(displayState, baseSegmentColoring ? objectId : rootObjectId, alpha) :
        undefined;
    callback(objectId, color, pickIndex, rootObjectId);
  });
}

export class CustomSegColorWidget extends RefCounted {
  element = document.createElement('div');
  color_squer = makeElement('div', ['color-squer']);
  color_input_r = makeElement('input', ['color-input-r'],{'type': 'number'}) as HTMLInputElement;
  color_input_g = makeElement('input', ['color-input-g'],{'type': 'number'}) as HTMLInputElement;
  color_input_b = makeElement('input', ['color-input-b'],{'type': 'number'}) as HTMLInputElement;
  color_input_hex = makeElement('input', ['color-input-hex']) as HTMLInputElement;
  platterDom = document.createElement('canvas');        //  左边大框颜色
  global = document.createElement('canvas');            //  中间圆圈
  colorBar = document.createElement('canvas');          //  右边颜色带
  canvasAre = makeElement('div');
  canvasLeft = document.createElement('div');
  canvasRight = makeElement('div',['canvas-right'],{'style': 'display: flex;justify-content: space-between;padding: 1rem 0'});
  gCtx: CanvasRenderingContext2D = this.global.getContext('2d') as CanvasRenderingContext2D;
  ctx: CanvasRenderingContext2D = this.platterDom.getContext('2d') as CanvasRenderingContext2D;
  cCtx:CanvasRenderingContext2D = this.colorBar.getContext('2d') as CanvasRenderingContext2D;
  sR = 0;
  sG = 255;
  sB = 255;
  xScale = Math.ceil(this.platterDom.width / 128);//向上取整方便绘制矩形
  yScale = Math.ceil(this.platterDom.height / 128);//向上取整方便绘制矩形
  scaleWidth = this.platterDom.width / this.xScale;
  scaleHeight = this.platterDom.height / this.yScale;
  pickLineWidth = 8;
  pickLineY: number;
  isInitColorBar:boolean = false;
  pickPoint:{[k:string]: number} = {};
  default_color_arr = [{ id: 0, color: '#D20000', rgb: 'rgb(210,0,0)' }, { id: 1, color: '#FF0000', rgb: 'rgb(255,0,0)' }, { id: 2, color: '#FFBE00', rgb: 'rgb(255,190,0)' }, { id: 3, color: '#FEFF00', rgb: 'rgb(254,255,0)' },
  { id: 4, color: '#7ED332', rgb: 'rgb(126,211,50)' }, { id: 5, color: '#00B442', rgb: 'rgb(210,0,0)' }, { id: 6, color: '#00B2F6', rgb: 'rgb(0,180,60)' }, { id: 7, color: '#0070C6', rgb: 'rgb(0,178,246)' }, { id: 8, color: '#001F64', rgb: 'rgb(0,112,198)' }, { id: 9, color: '#7A26A6', rgb: 'rgb(0,31,100)' },
  ];
  defaultColor = document.createElement('div');
  constructor(public model: vec3, public idString: string, public displaystate?: SegmentationUserLayerDisplayState) {
    super();
    const { element, platterDom, global, colorBar, canvasLeft, color_input_b, color_input_r, color_input_g, color_input_hex } = this;
    element.style.textAlign = 'center';
    element.addEventListener('click',(event:Event)=>{
      event.preventDefault()
    })
    platterDom.id = 'platterDom';
    global.id = 'global';
    colorBar.id = 'colorBar';
    canvasLeft.style.position = 'relative';
    canvasLeft.style.display = 'inline-block'
    this.initDom();
    this.initPlatter();
    this.draw();
    this.initBarHelper();
    this.colorBarDraw();
    this.makecolorlist();
    // this.registerDisposer(model.changed.add(() => this.updateView()));
    this.updateView();
    color_input_r.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_r.blur();
      }
    });
    color_input_g.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_g.blur();
      }
    });
    color_input_b.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_b.blur();
      }
    });
    color_input_hex.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_hex.blur();
      }
    });
    color_input_r.addEventListener('blur',()=>{
      if(color_input_r.valueAsNumber > 255 || color_input_r.valueAsNumber < 0 || isNaN(color_input_r.valueAsNumber)) return
      this.getDomOperate_changeColor_restore();
    });
    color_input_g.addEventListener('blur',()=>{
      if(color_input_g.valueAsNumber > 255 || color_input_g.valueAsNumber < 0 || isNaN(color_input_g.valueAsNumber)) return
      this.getDomOperate_changeColor_restore();
    });
    color_input_b.addEventListener('blur',()=>{
      if(color_input_b.valueAsNumber > 255 || color_input_b.valueAsNumber < 0 || isNaN(color_input_b.valueAsNumber)) return
      this.getDomOperate_changeColor_restore();
    });
    color_input_hex.addEventListener('blur',()=>{
      const value = this.color_input_hex.value;
      const b = value.match(`^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$`);
      if(!b) return
      this.color_squer.style.backgroundColor = value;
      this.getDomOperate_changeColor_restore(false);
      // this.updatePickLineGlobal( [this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber] );
      // this.updateModel();
    });
  }
  getDomOperate_changeColor_restore(flag:boolean = true){
    if(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0])(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
    this.changeSquerColor(flag);
    this.restoreSegColor();
    viewer.resetoreJson(viewer.state.toJSON());
  }
  changeSquerColor(flag:boolean = true){
    if(flag)this.color_squer.style.backgroundColor = rgbToHex(this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber)
    this.updatePickLineGlobal( [this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber] );
    this.updateModel();
  }
  async restoreSegColor(){
    if(viewer.binSize.value === 'cellbin'){
      viewer.self_newlabel_color[this.displaystate?.layer?.genefile as string][this.idString] = segSerializeColor(this.getRGB());
    }else{
      if(viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string] === undefined){
        viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string] = {};
        viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string][viewer.binSize.value as string] = {}
      }else if(viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string][viewer.binSize.value as string] === undefined)viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string][viewer.binSize.value as string] = {}
      viewer.bin200_self_newlabel_color[this.displaystate?.layer?.genefile as string][viewer.binSize.value as string][this.idString] = segSerializeColor(this.getRGB());
      let obj: any = {bin200_self_newlabel_color: viewer.bin200_self_newlabel_color};
      let tempData: any = await dbStorageMoreWindow.readByIndex('layerName', 'name', 'value'), data: any;
      tempData && (data = tempData.data);
      if (data && data.value) {
        obj.value = data.value;
      }
      if (dbStorageMoreWindow) {
        await dbStorageMoreWindow.update('layerName', {name: 'value', data: obj});
      }
      localStorage.setItem('layerName', new Date().toLocaleString());
    }
  }
  initDom(){
    const {platterDom, global, colorBar, canvasLeft, canvasRight, color_squer, color_input_hex, color_input_r, color_input_g, color_input_b, canvasAre} = this;
    platterDom.width = 240;
    platterDom.height = 180;
    global.width = 10;
    global.height = 10;
    colorBar.width = 184;
    colorBar.height = 25;
    canvasLeft.appendChild(platterDom)
    canvasLeft.appendChild(global);
    canvasRight.appendChild(colorBar);
    canvasRight.appendChild(color_squer);
    const bottom = makeElement('div', [], { 'style': 'display: flex;justify-content: space-between;' });
    const hex = makeElement('div', ['color-input']);
    const hexspan = makeElement('span', [], {}, `hex`);
    hex.appendChild(color_input_hex);
    hex.appendChild(hexspan);
    const ColorR = makeElement('div', ['color-input']);
    const ColorRspan = makeElement('span', [], {}, `R`);
    ColorR.appendChild(color_input_r);
    ColorR.appendChild(ColorRspan);
    const ColorG = makeElement('div', ['color-input']);
    const ColorGspan = makeElement('span', [], {}, `G`);
    ColorG.appendChild(color_input_g);
    ColorG.appendChild(ColorGspan);
    const ColorB = makeElement('div', ['color-input']);
    const ColorBspan = makeElement('span', [], {}, `B`);
    ColorB.appendChild(color_input_b);
    ColorB.appendChild(ColorBspan);
    bottom.appendChild(hex);
    bottom.appendChild(ColorR);
    bottom.appendChild(ColorG);
    bottom.appendChild(ColorB);
    canvasAre.appendChild(canvasLeft);
    canvasAre.appendChild(canvasRight);
    this.element.appendChild(canvasAre)
    this.element.appendChild(bottom);
  }
  initPlatter(){
    var that = this;
    this.platterDom.onmousedown = handleMouseEvent;
    this.global.onmousedown = handleMouseEvent;
    function handleMouseEvent(e: MouseEvent) {
      dragGlobal(e);
      document.onmousemove = function (e) {
        dragGlobal(e);
      }
      document.onmouseup = function () {
        that.restoreSegColor();
        viewer.resetoreJson(viewer.state.toJSON());
        document.onmousemove = null;
        document.onmouseup = null;
      }
    }
    function dragGlobal(e: MouseEvent) {
      var minX = Number( that.platterDom.getBoundingClientRect().left );
      var maxX = Number( minX + that.platterDom.width );
      var minY = Number( that.platterDom.getBoundingClientRect().top );
      var maxY = Number( minY + that.platterDom.height );
      var x = Number( e.x - that.global.width / 2 );
      var y = Number( e.y - that.global.height / 2 );
      if (x < minX) {
        x = minX;
      }
      else if (x > maxX) {
        x = maxX;
      }
      if (y < minY) {
        y = minY;
      }
      else if (y > maxY) {
        y = maxY;
      }
      var cx = x - that.platterDom.getBoundingClientRect().left;
      var cy = y -that.platterDom.getBoundingClientRect().top;
      that.global.style.left = cx - 5 + 'px';
      that.global.style.top = cy - 5 + "px";
      var col = that.getColor(cx, cy);
      that.pickPoint = { x: cx, y: cy };
      that.color_input_r.value = col.r.toString();
      that.color_input_g.value = col.g.toString();
      that.color_input_b.value = col.b.toString();
      that.color_input_hex.value = rgbToHex(col.r, col.g, col.b);
      that.color_squer.style.backgroundColor = rgbToHex(col.r, col.g, col.b);
      that.updateGlobal();
      that.updateModel();
      if(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0])(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
    }
  }
  updateGlobal () {
    this.gCtx!.clearRect(0, 0, this.global.width, this.global.height);
    this.gCtx!.strokeStyle = '#ffffff';
    this.gCtx!.lineWidth = 2;  
    this.gCtx!.beginPath();
    this.gCtx!.arc(5, 5, 5, 0,2*Math.PI);
    this.gCtx!.stroke();
  }
  draw() {
    this.xScale = Math.ceil(this.platterDom.width / 128);//向上取整方便绘制矩形
    this.yScale = Math.ceil(this.platterDom.height / 128);//向上取整方便绘制矩形
    this.scaleWidth = this.platterDom.width / this.xScale;
    this.scaleHeight = this.platterDom.height / this.yScale;
    //每个像素每个像素循环填充，计算太多，耗时严重。采取缩放，分块填充颜色的方式。当前约为128颜色级别。256以上会卡
    var xScale = this.xScale;
    var yScale = this.yScale;
    var w = this.scaleWidth;
    var h = this.scaleHeight;
    for (var x = 0; x < w; x++) {
      for (var y = 0; y < h; y++) {
        var col = this.getScaleColor(x, y);
        var color = 'rgb(' + col.r + ',' + col.g + ',' + col.b + ')';
        this.ctx!.fillStyle = color;
        this.ctx!.fillRect(x * xScale, y * yScale, xScale, yScale);
      }
    }
  }
  getScaleColor(scaleX: number, scaleY: number) {
    var w = this.scaleWidth;
    var h = this.scaleHeight;
    var r = 0;
    var g = 0;
    var b = 0;
    var sR = this.sR;
    var sG = this.sG;
    var sB = this.sB;
    var mid = sR > sG ? sR : sG;
    var max = mid > sB ? mid : sB;
    mid = mid > sB ? sB : mid;
    var min = sR + sG + sB - mid - max;
    var oneMaxXV = (255 - max) / w;
    // var oneMaxYV = (255 - max) / h;
    var oneMidXV = (255 - mid) / w;
    // var oneMidYV = (255 - mid) / h;
    var oneMinXV = (255 - min) / w;
    // var oneMinYV = (255 - min) / h;
    var midColor = 255 - scaleX * oneMidXV;
    var minColor = 255 - scaleX * oneMinXV;
    var maxColor = 255 - scaleX * oneMaxXV;
    var oneYTemp = midColor / h;
    var midC = Math.floor(midColor - scaleY * oneYTemp);
    oneYTemp = minColor / h;
    var minC = Math.floor(minColor - scaleY * oneYTemp);
    oneYTemp = maxColor / h;
    var maxC = Math.floor(maxColor - scaleY * oneYTemp);
    sR === max ? (r = maxC) : (sR === mid ? (r = midC) : (r = minC));
    sG === max ? (g = maxC) : (sG === mid ? (g = midC) : (g = minC));
    sB === max ? (b = maxC) : (sB === mid ? (b = midC) : (b = minC));
    return { r: r, g: g, b: b }
  }
  getColor(x: number, y: number) {
    return this.getScaleColor(x / this.xScale, y / this.yScale);
  }
  update(rgb:any) {
    this.sR = rgb.r;
    this.sG = rgb.g;
    this.sB = rgb.b;
    this.draw();
  }
  // 颜色带
  initBarHelper(){
    var that = this;
    this.colorBar.onmousedown = function (e: MouseEvent) {
      dragLine(e);
      document.onmousemove = function (e) {
        dragLine(e);
      }
      document.onmouseup = function () {
        that.restoreSegColor();
        viewer.resetoreJson(viewer.state.toJSON());
        document.onmousemove = null;
        document.onmouseup = null;
      }
    }
    function dragLine(e: MouseEvent) {
      var left = Math.round(that.colorBar.getBoundingClientRect().left);
      var y = e.x - left;
      that.updatePickLine(y);
      var col = that.getColorBarPickColor();
      that.update(col);
      col = that.getColor(that.pickPoint.x, that.pickPoint.y);
      that.color_input_r.value = col.r.toString();
      that.color_input_g.value = col.g.toString();
      that.color_input_b.value = col.b.toString();
      that.color_input_hex.value = rgbToHex(col.r, col.g, col.b);
      that.color_squer.style.backgroundColor = rgbToHex(col.r, col.g, col.b);
      that.updateGlobal();
      that.updateModel();
      if(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0])(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
    }
  }
  colorBarDraw(){
    this.drawRain();
  }
  drawRain(){
    var w = this.colorBar.width - this.pickLineWidth;
    var h = this.colorBar.height - this.pickLineWidth;
    var ctx = this.cCtx;
    var offY = this.pickLineWidth / 2;
    for (var y = 0; y <= w; y++) {
      var col = this.getColorBarColor(y);
      var color = 'rgb(' + col.r + ',' + col.g + ',' + col.b + ")";
      ctx!.strokeStyle = color;
      ctx!.beginPath();
      ctx!.moveTo(y + offY, this.pickLineWidth);
      ctx!.lineTo(y + offY, h);
      ctx!.stroke();
    }
  }
  getColorBarColor(y: number){
    var h = this.colorBar.width - this.pickLineWidth;;
    //每个像素颜色级别
    var oneYV = 255 / (h / 6);
    var r = 255;
    var g = 0;
    var b = 0;
    if (y <= h / 3) {
      //红-洋红
      if (y <= h / 6) {
        b = Math.floor(oneYV * y);
        if (b > 255) b = 255;
        r = 255;
      } else {
        //洋红-蓝
        r = 255 - Math.floor(oneYV * (y - h / 6));
        if (r < 0) r = 0;
        b = 255;
      }
      g = 0;
    } else if (y <= 2 * h / 3) {
      if (y < 3 * h / 6) {
        g = Math.floor(oneYV * (y - 2 * h / 6));
        if (g > 255) g = 255;
        b = 255;
      } else {
        b = 255 - Math.floor(oneYV * (y - 3 * h / 6));
        if (b < 0) b = 0;
        g = 255;
      }
      r = 0;
    } else {
      if (y < 5 * h / 6) {
        r = Math.floor(oneYV * (y - 4 * h / 6));
        if (r > 255) r = 255;
        g = 255;
      } else {
        g = 255 - Math.floor(oneYV * (y - 5 * h / 6));
        if (g < 0) g = 0;
        r = 255;
      }
      b = 0;
    }
    return { r: r, g: g, b: b };
  }
  drawPickLine (y: number) {
    this.pickLineY = y;
    var w = this.pickLineWidth;
    var ch = this.colorBar.height;
    var cw = this.colorBar.width;
    var ctx = this.cCtx;
    ctx.clearRect(0, 0, cw, w);
    ctx.clearRect(0, ch - w, cw, ch);
    ctx!.fillStyle = '#ffaaaa';
    ctx!.beginPath();
    ctx!.moveTo(y, 0);
    ctx!.lineTo(y + w / 2, w);
    ctx!.lineTo(y + w, 0);
    ctx!.fill();
    ctx!.beginPath();
    ctx.moveTo(y, ch);
    ctx.lineTo(y + w / 2, ch - w);
    ctx.lineTo(y + w, ch);
    ctx!.fill();
  }
  getColorBarPickColor () {
    return this.getColorBarColor(this.pickLineY);
  }
  updatePickLine (y: number) {
    var h = this.colorBar.width - this.pickLineWidth;
    var maxX = h;
    var minX = 0;
    if (y < minX) {
      y = minX;
    }
    else if (y > maxX) {
      y = maxX;
    }
    this.drawPickLine(y);
  }
  private getRGB() {
    return this.model;
  }
  private updateModel(value?:vec3) {
    this.model = value ? value : parseRGBColorSpecification(this.color_squer.style.backgroundColor);
    this.updateView();
  }
  private updateView() {
    this.color_squer.style.backgroundColor = segSerializeColor(this.getRGB());
    this.color_input_hex.value = segSerializeColor(this.getRGB());
    const rgb:number[] = set16ToRgb(segSerializeColor(this.getRGB()))! as number[];
    this.color_input_r.value = rgb[0].toString();
    this.color_input_g.value = rgb[1].toString();
    this.color_input_b.value = rgb[2].toString();
    const id = Uint64.parseString(String(this.idString));
    // const {globalStatedColor} = viewer;
    id.tryParseString(this.idString);
    const colorGroupState = this.displaystate!.segmentationColorGroupState.value;
    const {segmentStatedColors} = colorGroupState;
    const color = this.getRGB();
    segmentStatedColors.delete(id)
    segmentStatedColors.set(id, new Uint64(packColor(color)));
    const select_name = (this.displaystate as SegmentationUserLayerDisplayState).layer.genename;
    const cluster = select_name.includes('cell_cluster') ? select_name.replace('cell_cluster_layer','UMAP') : select_name.replace('UMAP','cell_cluster_layer')
    const umap = viewer.layerManager.managedLayers.filter((item:any)=> item.name === cluster)[0];
    if(umap){
      const umap_visibleSegments = ( (umap.layer as AnnotationUserLayer).displayState as SegmentationDisplayState).segmentationColorGroupState.value.segmentStatedColors;
      umap_visibleSegments.delete(id)
      umap_visibleSegments.set(id, new Uint64(packColor(color)));
      console.log(color)
    }
    // globalStatedColor.deleteValue(id)
    // globalStatedColor.setValue(id, new Uint64(packColor(color)));
    if(document.getElementsByClassName('color-widget-'+id)[0]){
      (document.getElementsByClassName('color-widget-'+id)[0] as HTMLInputElement).style.background = segSerializeColor(this.getRGB());;
    }
    if(!this.isInitColorBar){
      // 只调用一次
      const init_h = this.getColorBarHeight( rgb );
      this.updatePickLine(init_h);
      this.pickLineY = init_h;
      // var col = this.getColorBarPickColor();
      this.update({r:rgb[0],g:rgb[1],b:rgb[2]});
      this.getGlobalPosition();
      this.isInitColorBar = true;
    }
  }
  updatePickLineGlobal(rgb: number[]){
    const init_h = this.getColorBarHeight( rgb );
    this.updatePickLine(init_h);
    this.pickLineY = init_h;
    // var col = this.getColorBarPickColor();
    this.update({r:rgb[0],g:rgb[1],b:rgb[2]});
    this.getGlobalPosition();
  }
  getColorBarHeight(rgb: number[]){
    var h = this.colorBar.width - this.pickLineWidth;
    var oneYV = 255 / (h / 6);
    // 颜色带 1/6 : [255,0,255], 2/6: [0,0,255], 3/6: [0,255,255], 4/6: [0,255,0], 5/6: [255,255,0] 6/6: [255,0,0]
    const new_rgb = [ rgb[0] >= 255 / 2 ? 255 : 0,  rgb[1] >= 255 / 2 ? 255 : 0, rgb[2] >= 255 / 2 ? 255 : 0 ]
    const unit = Math.round(255/6);
    if(new_rgb[0] >= unit * 5 && new_rgb[2] >= unit * 5){
      // 第一格
      return rgb[2] / oneYV;
    }else if( new_rgb[1] >= unit * 5 && new_rgb[2] >= unit * 5){
      // 第三格
      return rgb[1] / oneYV + (h / 3);
    }else if(  new_rgb[0] >= unit * 5 &&  new_rgb[1] >= unit * 5 ){
      // 第五格
      return rgb[0] / oneYV + (4 * h / 6);
    }else if( new_rgb[0] <= unit &&  new_rgb[2] >= unit * 5 && new_rgb[1] <= unit){
      // 第二格
      let min = rgb[0] === 0 ? Math.max(rgb[1], rgb[2]) : 0;
      return min / oneYV + rgb[0] / oneYV + (h / 6);
    }else if(new_rgb[0] <= unit &&  new_rgb[1] >= unit * 5){
      // 第四格
      return rgb[2] / oneYV + (h / 2);
    }else if( new_rgb[0] >= unit * 5 &&  new_rgb[1] <= unit && new_rgb[2] <= unit ){
      // 第六格
      return rgb[0] / oneYV + (5 * h / 6);
    }
    return (rgb[0] / oneYV) + (rgb[1] / oneYV) + (rgb[2] / oneYV);
  }
  getGlobalPosition(){
    // let new_rgb = JSON.parse(JSON.stringify(rgb));
    // new_rgb.sort((a: number,b: number)=> b- a);
    // const index = rgb.indexOf(new_rgb[0]) === 1 ? 'g' : rgb.indexOf(new_rgb[0]) === 2 ? 'b' : 'r';
    // console.log(index)
    // var w = this.scaleWidth;
    // var h = this.scaleHeight;
    // for (var x = 0; x < w; x++) {
    //   for (var y = 0; y < h; y++) {
    //     var col = this.getScaleColor(x, y);
    //     if(Math.abs(col[index] - new_rgb[0]) < 5 ){
          this.global.style.left = this.platterDom.width - 5 + 'px';
          this.global.style.top = -5 + "px";
          this.updateGlobal();
          this.pickPoint = { x: this.platterDom.width - 5, y: 0 };
  //       }
  //     }
  //   }
  }
  private makecolorlist() {
    this.defaultColor.innerHTML = '';
    var ul = makeElement('ul', ['default-color-image-user-layer-ul'], { 'style': 'width: 244px;padding:0px;margin:0' });
    for (let i = 0; i < this.default_color_arr.length; i++) {
      const li = makeElement('li', ['default-color-image-user-layer-li'], { 'color': this.default_color_arr[i].color, 'style': 'background: ' + this.default_color_arr[i].color })
      li.addEventListener('click', () => {
        if (document.getElementsByClassName('default-color-image-user-layer-li-checked')[0]) {
          (ul.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
        }
        li.classList.add('default-color-image-user-layer-li-checked');
        this.isInitColorBar = false;
        this.updateModel(parseRGBColorSpecification(this.default_color_arr[i].color));
        this.restoreSegColor();
        viewer.resetoreJson(viewer.state.toJSON());
      });
      ul.appendChild(li);
    }
    this.defaultColor.appendChild(makeElement('p',[],{'style': 'text-align: left;margin-bottom: 0'},i18n.commonColors));
    this.defaultColor.appendChild(ul);
    this.defaultColor.classList.add('default-color-image-user-layer');
    this.element.appendChild(this.defaultColor);
  }
}

export function set16ToRgb(str: string, flag: boolean = true){
  var reg = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/
  if(!reg.test(str)){return;}
  let newStr = (str.toLowerCase()).replace(/\#/g,'')
  let len = newStr.length;
  if(len == 3){
      let t = ''
      for(var i=0;i<len;i++){
          t += newStr.slice(i,i+1).concat(newStr.slice(i,i+1))
      }
      newStr = t
  }
  let arr = []; //将字符串分隔，两个两个的分隔
  for(var i =0;i<6;i=i+2){
      let s = newStr.slice(i,i+2)
      arr.push(parseInt("0x" + s))
  }
  return !flag ? `rgb(${arr[0]},${arr[1]}, ${arr[2]})` : arr;
}

function rgbToHex (r: number, g: number, b: number) {
  const toHex = (num: number) => {
    const hex = num.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

function segSerializeColor(x: vec3|vec4) {
  if (x[3] === undefined || x[3] === 1 || x[3] === 0) {
    let result = '#';
    for (let i = 0; i < 3; ++i) {
      result += hexEncodeByte(Math.min(255, Math.max(0, Math.round(x[i] * 255))));
    }
    return result;
  } else {
    let result = 'rgba(';
    for (let i = 0; i < 3; ++i) {
      if (i !== 0) {
        result += ', ';
      }
      result += Math.min(255, Math.max(0, Math.round(x[i] * 255)));
    }
    result += `, ${float32ToString(x[3])})`;
    return result;
  }
}